import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, RequireAuth } from "@/lib/useAuth";
import { useTheme } from "@/hooks/use-theme";
import { BookshelfProvider } from "@/components/bookshelf";
import { CommunitiesProvider } from "@/components/communities";
import { MyWritingProvider } from "@/components/myWriting";
import { StoryBoardProvider } from "@/components/storyBoard";
import { CoverClubProvider } from "@/components/coverClub";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";
import Explore from "@/pages/Explore";
import Bookshelf from "@/pages/Bookshelf";
import BookDetails from "@/pages/BookDetails";
import Communities from "@/pages/Communities";
import CommunityDetail from "@/pages/CommunityDetail";
import MyWriting from "@/pages/MyWriting";
import StoryEditorPage from "@/pages/StoryEditorPage";
import StoryBoardPage from "@/pages/StoryBoardPage";
import CoverClub from "@/pages/CoverClub";
import Profile from "@/pages/Profile";

function AppToaster() {
  const { dark } = useTheme();
  return <Toaster theme={dark ? "dark" : "light"} position="top-center" richColors closeButton />;
}

// Old flat routes redirect into the /app/* product shell, so pre-existing
// links/bookmarks keep working after the authenticated-IA migration.
function RedirectToStudioStory() {
  const { storyId } = useParams();
  return <Navigate to={`/app/studio/${storyId}`} replace />;
}

function RedirectToCommunity() {
  const { communityId } = useParams();
  return <Navigate to={`/app/communities/${communityId}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BookshelfProvider>
        <CommunitiesProvider>
          <MyWritingProvider>
            <StoryBoardProvider>
              <CoverClubProvider>
                <AppToaster />
                <Routes>
                  <Route path="/" element={<SignIn />} />
                  <Route path="/signin" element={<SignIn />} />
                  <Route path="/signup" element={<SignUp />} />
                  <Route path="/signup/success" element={<SignUp />} />

                  {/* Authenticated product shell */}
                  <Route
                    path="/app/explore"
                    element={
                      <RequireAuth>
                        <Explore />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/app/bookshelf"
                    element={
                      <RequireAuth>
                        <Bookshelf />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/app/books/:bookId"
                    element={
                      <RequireAuth>
                        <BookDetails />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/app/studio"
                    element={
                      <RequireAuth>
                        <MyWriting />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/app/studio/:storyId"
                    element={
                      <RequireAuth>
                        <StoryEditorPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/app/studio/:storyId/board"
                    element={
                      <RequireAuth>
                        <StoryBoardPage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/app/communities"
                    element={
                      <RequireAuth>
                        <Communities />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/app/communities/:communityId"
                    element={
                      <RequireAuth>
                        <CommunityDetail />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/app/cover-club"
                    element={
                      <RequireAuth>
                        <CoverClub />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/app/profile"
                    element={
                      <RequireAuth>
                        <Profile />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/app/profile/:username"
                    element={
                      <RequireAuth>
                        <Profile />
                      </RequireAuth>
                    }
                  />

                  {/* Legacy flat-route redirects */}
                  <Route path="/home" element={<Navigate to="/app/explore" replace />} />
                  <Route path="/bookshelf" element={<Navigate to="/app/bookshelf" replace />} />
                  <Route path="/my-writing" element={<Navigate to="/app/studio" replace />} />
                  <Route path="/my-writing/:storyId" element={<RedirectToStudioStory />} />
                  <Route path="/communities" element={<Navigate to="/app/communities" replace />} />
                  <Route path="/communities/:communityId" element={<RedirectToCommunity />} />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </CoverClubProvider>
            </StoryBoardProvider>
          </MyWritingProvider>
        </CommunitiesProvider>
      </BookshelfProvider>
    </AuthProvider>
  );
}

export default App;
